import React, { useEffect, useState, useRef } from 'react';
import { Card, DatePicker, Select, Row, Col, Image, Typography, Modal, Empty } from 'antd';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { FiDownload } from "react-icons/fi";
import { highlightText } from 'components/utils/SearchHighlight';
import { Popover } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useCheckSessionHeartBeat } from "components/API/ApiEnpoints";
import { SessionExtend } from "components/utils/SessionCheckUtils";
import LogDisplayComponent from './LogDisplayComponent';
import { Pagination } from 'antd';
import FeedbackFloater, { FeedbackFloaterRef } from 'components/Floaters/FeedbackFloater';
import { EditOutlined } from '@ant-design/icons';
import axios from "axios";

const { Paragraph, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

// --- CSS TO APPLY THEME TO ANT DESIGN COMPONENTS (Dropdowns, Cards, Modals) ---
const antThemeOverrides = `
  /* 1. DROPDOWN FIXES */
  .ant-select-dropdown {
    background-color: #262626 !important;
    border: 1px solid #444 !important;
  }
  .ant-select-item-option-content {
    color: #ffffff !important;
  }
  .ant-select-item-option-active {
    background-color: #444444 !important;
  }
  .ant-select-item-option-selected {
    background-color: #4a90e2 !important;
    color: #ffffff !important;
  }
  .ant-picker-dropdown .ant-picker-panel-container {
    background-color: #262626 !important;
    color: #fff !important;
  }
  .ant-picker-header, .ant-picker-content th {
    color: #fff !important;
  }
  .ant-picker-cell-in-view {
    color: #ccc !important;
  }
  .ant-picker-cell-in-view.ant-picker-cell-selected .ant-picker-cell-inner {
    background-color: #4a90e2 !important;
  }

  /* 2. CARD FIXES (Background & Text) */
  .ant-card {
    background-color: var(--card-background) !important;
    color: var(--text-color) !important;
    border-color: #444 !important; /* Subtle border for dark mode */
  }
  .ant-card-head {
    border-bottom: 1px solid #444 !important;
    color: var(--text-color) !important;
  }
  .ant-card-head-title {
    color: var(--text-color) !important;
  }
  .ant-card-body {
    color: var(--text-color) !important;
  }

  /* 3. MODAL FIXES (Background & Text) */
  .ant-modal-content {
    background-color: var(--card-background) !important;
    color: var(--text-color) !important;
  }
  .ant-modal-header {
    background-color: var(--card-background) !important;
    border-bottom: 1px solid #444 !important;
  }
  .ant-modal-title {
    background-color: transparent !important;
    color: var(--text-color) !important;
  }
  .ant-modal-close {
    color: var(--text-color) !important;
  }
  .ant-modal-close:hover {
    color: #4a90e2 !important;
    background-color: rgba(255,255,255,0.1) !important;
  }

  /* 4. GENERAL TYPOGRAPHY FIX inside Ant Components */
  .ant-typography {
    color: var(--text-color) !important;
  }
`;

const PageWrapper = styled.div`
  padding: 24px;
  background-color: var(--page-color);
  color: var(--text-color);
`;

const Heading = styled.h2`
  font-size: 22px;
  margin-bottom: 1rem;
`;

const FilterBar = styled.div`
  background: var(--card-background);
  border-radius: 8px;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 12px;
`;

const ImagePreview = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
`;

const DownloadButton = styled.a`
  position: absolute;
  bottom: 4px;
  left: 4px;
  font-size: 12px;
  background: #fff;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid #888;
  text-decoration: none;
  color: #333;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f0f0f0;
    border-color: #555;
  }
`;

const TagGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const StyledTag = styled.span<{ variant?: string }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;

  background-color: ${({ variant }) =>
    variant === "type" ? "#e0f7fa" :
      variant === "component" ? "#f3e5f5" :
        variant === "status" ? "#fff3e0" :
          variant === "severity-high" ? "#ffebee" :
            variant === "severity-low" ? "#e8f5e9" :
              variant === "priority-high" ? "#fff8e1" :
                variant === "priority-low" ? "#ede7f6" :
                  "#f0f0f0"};

  color: ${({ variant }) =>
    variant === "type" ? "#00796b" :
      variant === "component" ? "#6a1b9a" :
        variant === "status" ? "#ef6c00" :
          variant === "severity-high" ? "#c62828" :
            variant === "severity-low" ? "#2e7d32" :
              variant === "priority-high" ? "#ff6f00" :
                variant === "priority-low" ? "#4527a0" :
                  "#333"};
`;

const StyledPrimaryButton = styled.button`
  background-color: #4a90e2;
  color: white;
  padding: 8px 18px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
  margin-left: 8px;

  &:hover {
    background-color: #3a7bd5;
  }

  &:disabled {
    background-color: #b0c4de;
    cursor: not-allowed;
  }
`;

const LoadingOverlay = styled.div<{ show: boolean }>`
  position: relative;
  
  ${({ show }) => show && `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(255, 255, 255, 0.7);
      backdrop-filter: blur(1px);
      z-index: 10;
      border-radius: 8px;
    }
    
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 20px;
      height: 20px;
      margin: -10px 0 0 -10px;
      border: 2px solid #f3f3f3;
      border-top: 2px solid #4a90e2;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      z-index: 11;
    }
  `}
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// --- NEW THEMED COMPONENTS ---

const StyledAntSelect = styled(Select)`
  .ant-select-selector {
    background-color: var(--card-background) !important;
    color: var(--text-color) !important;
    border: 1px solid #ccc !important;
  }
  
  .ant-select-selection-item {
    color: var(--text-color) !important;
  }
  
  .ant-select-arrow {
    color: var(--text-color) !important;
  }
  
  .ant-select-selection-placeholder {
    color: var(--text-color) !important;
    opacity: 0.6;
  }
  
  .ant-select-clear {
    background: var(--card-background) !important;
    color: var(--text-color) !important;
  }
`;

const StyledAntDatePicker = styled(DatePicker)`
  background-color: var(--card-background) !important;
  border: 1px solid #ccc !important;

  .ant-picker-input > input {
    color: var(--text-color) !important;
  }
  
  .ant-picker-input > input::placeholder {
    color: var(--text-color) !important;
    opacity: 0.6;
  }

  .ant-picker-suffix {
    color: var(--text-color) !important;
  }
  
  .ant-picker-clear {
    background: var(--card-background) !important;
    color: var(--text-color) !important;
  }
`;

const StyledSelect = styled.select`
  width: 160px;
  height: 38px;
  padding: 0 10px;
  border-radius: 4px;
  border: 1px solid #ccc;
  
  /* Use CSS Variables so it adapts to Dark/Light mode automatically */
  background-color: var(--card-background); 
  color: var(--text-color); 

  /* 2. Styles for the dropdown options */
  & option {
    background-color: var(--card-background);
    color: var(--text-color);
  }
`;

interface FeedbackEntry {
  id: string;
  timestamp: string;
  description: string;
  image_uids?: string[];
  images?: string[];
  feedback_type?: string;
  component_type?: string;
  feedback_status?: string;
  severity?: string;
  priority?: string;
  added_by?: string;
  action_message?: string;
}

const AdminFeedbackPage: React.FC = () => {
  const feedbackFloaterRef = useRef<FeedbackFloaterRef>(null);
  useCheckSessionHeartBeat();
  const org_Uid = localStorage.getItem("org_uid");
  const [feedbacks, setFeedbacks] = useState<FeedbackEntry[]>([]);
  const [filtered, setFiltered] = useState<FeedbackEntry[]>([]);
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');

  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackEntry | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const userRole = localStorage.getItem("user_role");
  const isAdmin = userRole === "orgadmin" || userRole === "systemadmin";
  const [activeView, setActiveView] = useState<'feedback' | 'logs'>('feedback');
  const [loading, setLoading] = useState(true);
  const [filterOptionsRaw, setFilterOptionsRaw] = useState<any>(null);
  const [filterOptions, setFilterOptions] = useState<{ [key: string]: string[] }>({});
  const [selectedFeedbackType, setSelectedFeedbackType] = useState("bugs");
  const [selectedComponentType, setSelectedComponentType] = useState<string | undefined>();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [isGuideVisible, setGuideVisible] = useState(false);
  const [paginationLoading, setPaginationLoading] = useState(false);

  const [feedbackFilter, setFeedbackFilter] = useState<any>(null);

  const [selectedOrg, setSelectedOrg] = useState<string | undefined>(undefined);
  const [severity, setSeverity] = useState<string | undefined>(undefined);
  const [priority, setPriority] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [isTagGuideModalOpen, setIsTagGuideModalOpen] = useState(false);


  const [selectedSeverity, setSelectedSeverity] = useState<string | undefined>();
  const [selectedPriority, setSelectedPriority] = useState<string | undefined>();
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>();
  const [totalCount, setTotalCount] = useState(0);
  const [isLogsMode, setIsLogsMode] = useState(false);
  const [tempDateRange, setTempDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);

  const [orgUid, setOrgUid] = useState<string>("");
  const privilege = localStorage.getItem("privilege");
  const beAddress =
    window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;
  const sessionUid = localStorage.getItem("session_uid") || "";
  const userUid = localStorage.getItem("user_uid") || "";


  useEffect(() => {
    const fetchOrg = async () => {
      try {
        const response = await axios.get(`${beAddress}/api/org-info`, {
          headers: {
            "user-uid": userUid,
          },
          withCredentials: true,
        });

        // FIX: strictly check for "Success" and that data is an Array (Admin case)
        if (
          response.status === 200 &&
          response.data?.status === "Success" &&
          Array.isArray(response.data.data) &&
          response.data.data.length > 0
        ) {
          setOrgUid(response.data.data[0].org_uid);
          return;
        }
      } catch (err) {
        console.warn("Org info API failed, falling back to localStorage");
      }

      // ✅ FALLBACK FOR NORMAL USER (Runs if API fails OR if status is not 'Success')
      const storedOrgUid = localStorage.getItem("org_uid");
      if (storedOrgUid) {
        setOrgUid(storedOrgUid);
      }
    };

    fetchOrg();
  }, [beAddress, userUid]);


  useEffect(() => {
    if (orgUid) {
      fetchFeedbacks();
    }
  }, [orgUid]);

  useEffect(() => {
    if (orgUid) {
      fetchFeedbacks();
    }
  }, [orgUid]);

  useEffect(() => {
    let result = [...feedbacks];

    if (dateRange && dateRange[0] && dateRange[1]) {
      result = result.filter(fb => {
        const fbTime = dayjs(fb.timestamp);
        return fbTime.isAfter(dateRange[0]!.startOf('day')) && fbTime.isBefore(dateRange[1]!.endOf('day'));
      });
    }

    if (searchTerm.trim()) {
      const lowerSearch = searchTerm.toLowerCase();
      result = result.filter(fb =>
        fb.description.toLowerCase().includes(lowerSearch)
      );
    }

    if (activeView === 'feedback') {
      result = result.filter(fb => fb.feedback_type !== 'logs');
    } else {
      result = result.filter(fb => fb.feedback_type === 'logs');
    }

    result.sort((a, b) =>
      sortOrder === 'newest'
        ? dayjs(b.timestamp).unix() - dayjs(a.timestamp).unix()
        : dayjs(a.timestamp).unix() - dayjs(b.timestamp).unix()
    );

    setFiltered(result);
  }, [dateRange, sortOrder, feedbacks, searchTerm, activeView]);

  useEffect(() => {
    const orgList = feedbackFilter?.org_uid_list || [];
    if (orgList.length === 1 && !selectedOrg) {
      setSelectedOrg(orgList[0]);
    }
  }, [feedbackFilter, selectedOrg]);

  const fetchFilters = async () => {
    try {
      const url = new URL(`${process.env.REACT_APP_API_URL}/api/feedback-filter`);
      url.searchParams.append("filter_type", "get");

      const res = await fetch(url.toString(), {
        headers: {
          "user-uid": localStorage.getItem("user_uid") || "",
        }, credentials: "include",
      });

      const data = await res.json();
      if (data?.status === "Success") {
        setFeedbackFilter(data.data);
      }
    } catch (error) {
      console.error("Failed to fetch filters:", error);
    }
  };

  useEffect(() => {
    const init = async () => {
      await fetchFilters();
      setSelectedFeedbackType("bugs");
    };
    init();
  }, []);

  const fetchFeedbacks = async () => {
    setSelectedFeedback(null);
    setLoading(true);

    try {
      const url = new URL(`${process.env.REACT_APP_API_URL}/api/feedback`);

      // Append selected filters
      if (selectedFeedbackType) url.searchParams.append("feedback_type_list", selectedFeedbackType);
      if (selectedComponentType) url.searchParams.append("component_type_list", selectedComponentType);
      if (selectedSeverity) url.searchParams.append("severity_list", selectedSeverity);
      if (selectedPriority) url.searchParams.append("priority_list", selectedPriority);
      if (selectedStatus) url.searchParams.append("feedback_status_list", selectedStatus);
      if (selectedOrg) url.searchParams.append("org_uid_list", selectedOrg);
      url.searchParams.append("org_uid", orgUid);

      if (dateRange && dateRange[0] && dateRange[1]) {
        url.searchParams.set("from_datetime", dateRange[0].toISOString());
        url.searchParams.set("to_datetime", dateRange[1].toISOString());
      }

      // Pagination
      url.searchParams.set("page", currentPage.toString());
      url.searchParams.set("page_size", pageSize.toString());

      const res = await fetch(url.toString(), {
        headers: {
          "user-uid": localStorage.getItem("user_uid") || "",
        },
        credentials: "include",
      });

      const data = await res.json();
      if (data.status === "Success" && data.data?.feedback_records) {
        const entries = data.data.feedback_records;
        setTotalCount(data?.data?.total_count || 0);
        const allImageUIDs = entries.flatMap((entry: any) => entry.image_uid_list || []);
        const uniqueImageUIDs = [...new Set(allImageUIDs)] as string[];

        let imageMap: Record<string, string> = {};
        if (uniqueImageUIDs.length > 0) {
          const imageUrl = new URL(`${process.env.REACT_APP_API_URL}/api/get-image-by-uid`);
          uniqueImageUIDs.forEach(uid => imageUrl.searchParams.append("image_uid_list", uid));
          imageUrl.searchParams.set("is_icon_img", "false");
          imageUrl.searchParams.set("is_feedback_image", "true");

          const imgRes = await fetch(imageUrl.toString(), {
            headers: {
              "user-uid": localStorage.getItem("user_uid") || "",
              "session-uid": localStorage.getItem("session_uid") || "",
            },
          });

          const imgData = await imgRes.json();
          if (imgData.status === "Success" && Array.isArray(imgData.data)) {
            imageMap = Object.fromEntries(
              imgData.data.map((item: any) => {
                let mimeType = "image/png";
                if (item.image.startsWith("R0lGODlh")) mimeType = "image/gif";
                else if (item.image.startsWith("/9j/")) mimeType = "image/jpeg";
                else if (item.image.startsWith("iVBORw0KGgo")) mimeType = "image/png";
                return [item.image_uid, `data:${mimeType};base64,${item.image}`];
              })
            );
          }
        }

        const mapped: FeedbackEntry[] = entries.map((item: any) => ({
          id: item.feedback_uid,
          timestamp: item.added_at,
          description: item.feedback_description,
          image_uids: item.image_uid_list,
          images: (item.image_uid_list || []).map((uid: string) => imageMap[uid]).filter(Boolean),
          feedback_type: item.feedback_type,
          component_type: item.component_type,
          feedback_status: item.feedback_status,
          severity: item.severity,
          priority: item.priority,
          added_by: item.added_by,
          action_message: item.action_message,
        }));

        setFeedbacks(mapped);
      }

      SessionExtend();
    } catch (error) {
      console.error("Error fetching feedbacks:", error);
    } finally {
      setLoading(false);
    }
  };

  const disabledDate = (current: dayjs.Dayjs) => {
    const [start, end] = dateRange || [];
    if (!start) return false;
    return end === null && current.isBefore(start.startOf("day"));
  };

  useEffect(() => {
    let result = [...feedbacks];
    if (dateRange && dateRange[0] && dateRange[1]) {
      result = result.filter(fb => {
        const fbTime = dayjs(fb.timestamp);
        return fbTime.isAfter(dateRange[0]!.startOf('day')) && fbTime.isBefore(dateRange[1]!.endOf('day'));
      });
    }
    result.sort((a, b) =>
      sortOrder === 'newest'
        ? dayjs(b.timestamp).unix() - dayjs(a.timestamp).unix()
        : dayjs(a.timestamp).unix() - dayjs(b.timestamp).unix()
    );
    setFiltered(result);
  }, [dateRange, sortOrder, feedbacks]);

  const applyFilters = async (page = 1) => {
    if (selectedFeedbackType === "logs") {
      setIsLogsMode(true);
      setActiveView("logs");
    } else {
      setIsLogsMode(false);
      setActiveView("feedback");
    }

    setSelectedFeedback(null);
    setCurrentPage(page);

    if (page === 1) {
      setLoading(true);
    } else {
      setPaginationLoading(true);
    }

    const params = new URLSearchParams();

    if (selectedOrg) params.append("org_uid_list", selectedOrg);
    if (selectedFeedbackType) params.append("feedback_type_list", selectedFeedbackType);
    if (selectedComponentType) params.append("component_type_list", selectedComponentType);
    if (severity) params.append("severity_list", severity);
    if (priority) params.append("priority_list", priority);
    if (status) params.append("feedback_status_list", status);
    if (orgUid) params.append("org_uid", orgUid);

    if (tempDateRange?.[0]) {
      const from = dayjs(tempDateRange[0]).format("YYYY-MM-DDTHH:mm:ss");
      params.append("from_datetime", from);
    } if (selectedOrg) params.append("org_uid_list", selectedOrg);


    if (tempDateRange?.[1]) {
      const to = dayjs(tempDateRange[1]).format("YYYY-MM-DDTHH:mm:ss");
      params.append("to_datetime", to);
    }
    if (searchTerm) {
      params.append("search_by", searchTerm);
      params.append("search_on", "description");
    }

    params.append("page", page.toString());
    params.append("page_size", "10");

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/feedback?${params.toString()}`,
        {
          headers: {
            "user-uid": localStorage.getItem("user_uid") || "",
          }, credentials: "include",
        }
      );

      const data = await res.json();
      const entries = data?.data?.feedback_records || [];
      setTotalCount(data?.data?.total_count || 0);

      const allImageUIDs = entries.flatMap((entry: any) => entry.image_uid_list || []);
      const uniqueImageUIDs = [...new Set(allImageUIDs)] as string[];

      let imageMap: Record<string, string> = {};
      if (uniqueImageUIDs.length > 0) {
        const imageUrl = new URL(`${process.env.REACT_APP_API_URL}/api/get-image-by-uid`);
        uniqueImageUIDs.forEach(uid =>
          imageUrl.searchParams.append("image_uid_list", uid)
        );
        imageUrl.searchParams.set("is_icon_img", "false");
        imageUrl.searchParams.set("is_feedback_image", "true");
        if (selectedOrg) {
          imageUrl.searchParams.set("org_uid", selectedOrg);
        } else if (orgUid) {
          imageUrl.searchParams.set("org_uid", orgUid);
        }

        const imgRes = await fetch(imageUrl.toString(), {
          headers: {
            "user-uid": localStorage.getItem("user_uid") || "",
            "session-uid": localStorage.getItem("session_uid") || "",
          },
        });

        const imgData = await imgRes.json();
        if (imgData.status === "Success" && Array.isArray(imgData.data)) {
          imageMap = Object.fromEntries(
            imgData.data.map((item: any) => {
              let mimeType = "image/png";
              if (item.image.startsWith("R0lGODlh")) mimeType = "image/gif";
              else if (item.image.startsWith("/9j/")) mimeType = "image/jpeg";
              else if (item.image.startsWith("iVBORw0KGgo")) mimeType = "image/png";
              return [item.image_uid, `data:${mimeType};base64,${item.image}`];
            })
          );
        }
      }

      // Map fetched feedbacks
      const mapped: FeedbackEntry[] = entries.map((item: any) => ({
        id: item.feedback_uid,
        timestamp: item.added_at,
        description: item.feedback_description,
        image_uids: item.image_uid_list,
        images: (item.image_uid_list || [])
          .map((uid: string) => imageMap[uid])
          .filter(Boolean),
        feedback_type: item.feedback_type,
        component_type: item.component_type,
        feedback_status: item.feedback_status,
        severity: item.severity,
        priority: item.priority,
        added_by: item.added_by,
        action_message: item.action_message,
      }));

      setFeedbacks(mapped);
    } catch (err) {
      console.error("Failed to fetch filtered feedbacks", err);
    } finally {
      setLoading(false);
      setPaginationLoading(false);
    }
  };

  return (
    <PageWrapper>
      {/* INJECTED STYLES FOR DROPDOWN, CARD, AND MODAL THEMING */}
      <style>{antThemeOverrides}</style>

      <Heading>Feedback Overview</Heading>

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          {/* Search applies instantly */}
          <input
            type="text"
            placeholder="Search feedback..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: "8px 12px",
              borderRadius: 6,
              border: "1px solid #ccc",
              width: 220,
              backgroundColor: "var(--card-background)",
              color: "var(--text-color)"
            }}
          />

          <StyledSelect
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value as "newest" | "oldest")}
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
          </StyledSelect>
        </div>

        <Text strong style={{ color: "var(--text-color)" }}>Icon Guide</Text>
        <InfoCircleOutlined
          style={{ fontSize: 16, color: "#888", cursor: "pointer" }}
          onClick={() => setGuideVisible(true)}
        />

        <Modal
          open={isGuideVisible}
          title="Feedback Tag Guide"
          footer={null}
          onCancel={() => setGuideVisible(false)}
          centered
          bodyStyle={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            maxHeight: "70vh",
            overflowY: "auto",
          }}
        >
          {/* 🔴 SEVERITY */}
          <div>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>🔴 Severity Levels</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {(feedbackFilter?.severity_list || []).map((sev: string) => (
                <StyledTag key={sev} variant={`severity-${sev}`}>
                  {sev.charAt(0).toUpperCase() + sev.slice(1)}
                </StyledTag>
              ))}
            </div>
          </div>

          {/* ⚡ PRIORITY */}
          <div>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>⚡ Priority Levels</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {(feedbackFilter?.priority_list || []).map((pri: string) => (
                <StyledTag key={pri} variant={`priority-${pri}`}>
                  {pri.charAt(0).toUpperCase() + pri.slice(1)}
                </StyledTag>
              ))}
            </div>
          </div>

          {/* 📌 STATUS */}
          <div>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>📌 Status Options</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {(feedbackFilter?.status_list || []).map((st: string) => (
                <StyledTag key={st} variant="status">
                  {st.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                </StyledTag>
              ))}
            </div>
          </div>

          {/* 💬 FEEDBACK TYPES */}
          <div>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>💬 Feedback Types</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {Object.keys(feedbackFilter?.filter_map || {}).map((type: string) => (
                <StyledTag key={type} variant="type">
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </StyledTag>
              ))}
            </div>
          </div>

        </Modal>
      </div>

      <FilterBar>
        {/* RIGHT SIDE: Advanced Filters (Apply needed) */}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", width: "100%" }}>

          {/* Org Dropdown */}
          <StyledAntSelect
            placeholder="Select Org"
            value={selectedOrg || orgUid}
            onChange={(val) => setSelectedOrg(val as string)}
            style={{ width: 180 }}
            allowClear
            dropdownStyle={{ backgroundColor: "#262626" }} // Fallback
          >
            {orgUid && <Option key={orgUid} value={orgUid}>{orgUid}</Option>}
          </StyledAntSelect>

          {/* Date Range */}
          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            <StyledAntDatePicker
              value={tempDateRange?.[0] || null}
              onChange={(date: any) => {
                setTempDateRange([
                  date || null,
                  tempDateRange?.[1] || null,
                ]);
              }}
              placeholder="From Date"
            />

            <StyledAntDatePicker
              value={tempDateRange?.[1] || null}
              onChange={(date: any) => {
                setTempDateRange([
                  tempDateRange?.[0] || null,
                  date ? date.endOf("day") : null,
                ]);
              }}
              placeholder="To Date"
              disabledDate={(current) => {
                if (!tempDateRange?.[0]) return false;
                return current.isBefore(tempDateRange[0].startOf("day"));
              }}
            />
          </div>

          {/* Feedback Type */}
          <StyledAntSelect
            placeholder="Feedback Type"
            value={selectedFeedbackType ? `Feedback Type: ${selectedFeedbackType.charAt(0).toUpperCase() + selectedFeedbackType.slice(1)}` : undefined}
            onChange={(val) => {
              if (!val) {
                setSelectedFeedbackType("");
                setSelectedComponentType(undefined);
                return;
              }
              const cleanValue = (val as string).replace("Feedback Type: ", "");
              setSelectedFeedbackType(cleanValue);

              if (cleanValue === "logs") {
                const firstComponent = feedbackFilter?.filter_map?.logs?.[0] || undefined;
                setSelectedComponentType(firstComponent);
              } else {
                setSelectedComponentType(undefined);
              }
            }}
            style={{ width: 200 }}
            allowClear
          >
            {Object.keys(feedbackFilter?.filter_map || {}).map((type: string) => {
              const label = type.charAt(0).toUpperCase() + type.slice(1);
              return (
                <Option key={type} value={`Feedback Type: ${type}`}>
                  {label}
                </Option>
              );
            })}
          </StyledAntSelect>

          {/* Component Type */}
          <StyledAntSelect
            placeholder="Component Type"
            value={selectedComponentType ? `Component: ${selectedComponentType}` : undefined}
            onChange={(val) => {
              if (!val) {
                setSelectedComponentType(undefined);
                return;
              }
              const cleanValue = (val as string).replace("Component: ", "");
              setSelectedComponentType(cleanValue);
            }}
            style={{ width: 220 }}
            allowClear
            disabled={!selectedFeedbackType}
          >
            {(feedbackFilter?.filter_map?.[selectedFeedbackType] || []).map((component: string) => (
              <Option key={component} value={`Component: ${component}`}>
                Component: {component}
              </Option>
            ))}
          </StyledAntSelect>

          {/* Severity */}
          <StyledAntSelect
            placeholder="Severity"
            value={severity ? `Severity: ${severity}` : undefined}
            onChange={(val) => {
              if (!val) {
                setSeverity(undefined);
                return;
              }
              const cleanValue = (val as string).replace("Severity: ", "");
              setSeverity(cleanValue);
            }}
            style={{ width: 180 }}
            allowClear
          >
            {(feedbackFilter?.severity_list || []).map((level: string) => (
              <Option key={level} value={`Severity: ${level}`}>
                Severity: {level}
              </Option>
            ))}
          </StyledAntSelect>

          {/* Priority */}
          <StyledAntSelect
            placeholder="Priority"
            value={priority ? `Priority: ${priority}` : undefined}
            onChange={(val) => {
              if (!val) {
                setPriority(undefined);
              } else {
                const cleanValue = (val as string).replace("Priority: ", "");
                setPriority(cleanValue);
              }
            }}
            style={{ width: 160 }}
            allowClear
          >
            {(feedbackFilter?.priority_list || []).map((p: string) => (
              <Option key={p} value={`Priority: ${p}`}>
                Priority: {p}
              </Option>
            ))}
          </StyledAntSelect>

          {/* Status */}
          <StyledAntSelect
            placeholder="Status"
            value={status ? `Status: ${status}` : undefined}
            onChange={(val) => {
              if (!val) {
                setStatus(undefined);
              } else {
                const cleanValue = (val as string).replace("Status: ", "");
                setStatus(cleanValue);
              }
            }}
            style={{ width: 160 }}
            allowClear
          >
            {(feedbackFilter?.status_list || []).map((s: string) => (
              <Option key={s} value={`Status: ${s}`}>
                Status: {s}
              </Option>
            ))}
          </StyledAntSelect>

          {/* APPLY BUTTON */}
          <StyledPrimaryButton onClick={() => applyFilters()}>
            Apply Filters
          </StyledPrimaryButton>
        </div>
      </FilterBar>

      {loading ? (
        <Paragraph type="secondary" style={{ textAlign: 'center', marginTop: 40 }}>
          Loading...
        </Paragraph>
      ) : filtered.length > 0 ? (
        <LoadingOverlay show={paginationLoading}>
          {activeView === 'logs' ? (
            <LogDisplayComponent
              logs={filtered.map((fb) => ({
                id: fb.id,
                timestamp: fb.timestamp,
                description: fb.description,
                added_by: fb.added_by || "—",
                full: fb,
              }))}
            />
          ) : (
            <Row gutter={[16, 16]}>
              {filtered.map((feedback) => (
                <Col xs={24} sm={12} md={8} lg={6} key={feedback.id}>
                  <Card
                    title={dayjs(feedback.timestamp).format('MMM D, YYYY h:mm A')}
                    bordered
                    hoverable
                    onClick={() => setSelectedFeedback(feedback)}
                    style={{
                      cursor: 'pointer',
                      opacity: paginationLoading ? 0.7 : 1,
                      transition: 'opacity 0.3s ease'
                      /* NOTE: background/color logic is now handled by antThemeOverrides at the top */
                    }}
                  >
                    <Paragraph ellipsis={{ rows: 2 }}>
                      {highlightText(feedback.description, searchTerm)}
                    </Paragraph>

                    <TagGroup>
                      {feedback.feedback_type && (
                        <StyledTag variant="type">{feedback.feedback_type}</StyledTag>
                      )}
                      {feedback.component_type && (
                        <StyledTag variant="component">{feedback.component_type}</StyledTag>
                      )}
                      {feedback.feedback_status && (
                        <StyledTag variant="status">{feedback.feedback_status}</StyledTag>
                      )}
                      {feedback.severity && (
                        <StyledTag variant={`severity-${feedback.severity}`}>
                          Severity: {feedback.severity}
                        </StyledTag>
                      )}
                    </TagGroup>

                    {feedback.images?.length ? (
                      <ImagePreview onClick={(e) => e.stopPropagation()}>
                        {feedback.images.map((img, idx) => (
                          <Image
                            key={idx}
                            width={60}
                            height={60}
                            src={img}
                            style={{ objectFit: 'cover' }}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          />
                        ))}
                      </ImagePreview>
                    ) : null}
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </LoadingOverlay>
      ) : (
        <Paragraph type="secondary" style={{ textAlign: 'center', marginTop: 40, color: "var(--text-color)" }}>
          No {activeView === 'logs' ? 'log entries' : 'feedback entries'} found, try applying filters
        </Paragraph>
      )}

      {selectedFeedback && (
        <Modal
          title={
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", color: "var(--text-color)" }}>
              <span>
                {dayjs(selectedFeedback.timestamp).format('MMMM D, YYYY h:mm A')}
              </span>
              {privilege === "systemadmin" && (
                <EditOutlined
                  style={{ fontSize: 20, color: "#225963;", cursor: "pointer", marginRight: "330px" }}
                  title="Edit Feedback"
                  onClick={() => {
                    const feedbackWithOrg = {
                      ...selectedFeedback,
                      org_uid: orgUid,
                    };

                    setSelectedFeedback(null);
                    feedbackFloaterRef.current?.openWithData(feedbackWithOrg);
                  }}
                />
              )}
            </div>
          }
          open={selectedFeedback !== null && Object.keys(selectedFeedback).length > 0}
          onCancel={() => setSelectedFeedback(null)}
          footer={null}
          width={600}
        >
          {selectedFeedback && (
            <div style={{ padding: '12px' }}>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Description:</Text>
                <Paragraph style={{ marginTop: 8 }}>
                  {selectedFeedback.description}
                </Paragraph>
              </div>

              {selectedFeedback.action_message && (
                <div style={{
                  marginBottom: 16,
                  padding: "12px",
                  border: "1px solid #444",
                  borderRadius: "6px",
                  backgroundColor: "rgba(74, 144, 226, 0.1)" // Subtle blue tint
                }}>
                  <Text strong style={{ color: "#4a90e2" }}>Action Taken:</Text>
                  <Paragraph style={{ marginTop: 4, marginBottom: 0 }}>
                    {selectedFeedback.action_message}
                  </Paragraph>
                </div>
              )}

              <div style={{ marginBottom: 16 }}>
                <Text strong>Type:</Text> <Text>{selectedFeedback.feedback_type || "—"}</Text><br />
                <Text strong>Status:</Text> <Text>{selectedFeedback.feedback_status || "—"}</Text><br />
                <Text strong>Severity:</Text> <Text>{selectedFeedback.severity || "—"}</Text><br />
                <Text strong>Priority:</Text> <Text>{selectedFeedback.priority || "—"}</Text><br />
                <Text strong>Component:</Text> <Text>{selectedFeedback.component_type || "—"}</Text><br />
                <Text strong>Submitted by:</Text> <Text>{selectedFeedback.added_by || "—"}</Text>
              </div>

              <div style={{ marginBottom: 12 }}>
                <Text strong>Attached Images:</Text>
                {selectedFeedback.images?.length ? (
                  <ImagePreview>
                    {selectedFeedback.images.map((img, idx) => (
                      <div key={idx} style={{ position: 'relative' }}>
                        <Image
                          width={100}
                          height={100}
                          src={img}
                          style={{
                            objectFit: 'cover',
                            borderRadius: 6,
                            border: '1px solid #ccc',
                          }}
                        />
                        <a
                          href={img}
                          download={`feedback-image-${idx + 1}.png`}
                          style={{
                            position: 'absolute',
                            bottom: '4px',
                            left: '4px',
                            background: '#ffffffcc',
                            padding: '4px',
                            borderRadius: '50%',
                            border: '1px solid #888',
                            textDecoration: 'none',
                            color: '#333',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                            transition: 'all 0.2s ease',
                          }}
                          onMouseOver={(e) => {
                            (e.currentTarget as HTMLElement).style.background =
                              '#f0f0f0';
                            (e.currentTarget as HTMLElement).style.borderColor = '#555';
                          }}
                          onMouseOut={(e) => {
                            (e.currentTarget as HTMLElement).style.background =
                              '#ffffffcc';
                            (e.currentTarget as HTMLElement).style.borderColor = '#888';
                          }}
                        >
                          <FiDownload size={16} />
                        </a>
                      </div>
                    ))}
                  </ImagePreview>
                ) : (
                  <Paragraph type="secondary" style={{ marginTop: 8 }}>
                    No images attached
                  </Paragraph>

                )}
              </div>
            </div>
          )}
        </Modal>
      )}

      {!loading && filtered.length > 0 && (
        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <Pagination
            current={currentPage}
            pageSize={10}
            total={totalCount}
            onChange={(page) => applyFilters(page)}
            style={{
              marginTop: 20,
              textAlign: "center",
              opacity: paginationLoading ? 0.7 : 1,
              transition: 'opacity 0.3s ease'
            }}
            disabled={paginationLoading}
            showSizeChanger={false}
            showQuickJumper={false}
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} of ${total} items${paginationLoading ? ' (Loading...)' : ''}`
            }
          />
        </div>
      )}
      <FeedbackFloater
        ref={feedbackFloaterRef}
        onFeedbackUpdated={fetchFeedbacks}
      />
    </PageWrapper>
  );
};

export default AdminFeedbackPage;